import type { Submodel } from '../Metamodel/Submodel';
import type { SubmodelElement } from '../Metamodel/SubmodelElements/SubmodelElement';
import { SubmodelElementNode } from './SubmodelElementNode';

export class SubmodelTree {
  private _elementMap: Map<string, SubmodelElementNode> = new Map();

  constructor(
    public submodel: Submodel,
  ) { }

  delete(idShort: string): SubmodelElementNode | undefined {
    const element = this._elementMap.get(idShort);
    if (element) {
      this._elementMap.delete(idShort);
      element.removeFromSubmodel();
    }
    return element;
  }

  set(element: SubmodelElement): SubmodelTree {
    if (!element.idShort) {
      throw new Error('Element must have an idShort.');
    }
    if (this._elementMap.has(element.idShort)) {
      this.delete(element.idShort);
    }
    const childNode: SubmodelElementNode = SubmodelElementNode.from(element);
    this._elementMap.set(element.idShort, childNode);
    childNode.addToSubmodel(this);
    return this;
  }

  has(idShort: string): boolean {
    return this._elementMap.has(idShort);
  }

  get(idShort: string): SubmodelElement | undefined {
    return this._elementMap.get(idShort)?.element;
  }

  clear() {
    this._elementMap.forEach((elementNode) => {
      elementNode.removeFromSubmodel();
    });
    this._elementMap.clear();
  }

  keys(): string[] {
    return Array.from(this._elementMap.keys());
  }

  values(): SubmodelElement[] {
    return Array.from(this._elementMap.values()).map(node => node.element);
  }

  entries(): [string, SubmodelElement][] {
    return Array.from(this._elementMap.entries()).map(([key, node]) => [key, node.element]);
  }

  forEach(callback: (element: SubmodelElement, idShort: string) => void): void {
    this._elementMap.forEach((node, idShort) => {
      callback(node.element, idShort);
    });
  }

  get size(): number {
    return this._elementMap.size;
  }

  [Symbol.iterator](): IterableIterator<[string, SubmodelElement]> {
    return this.entries()[Symbol.iterator]();
  }

  deepSearch(
    predicate?: (element: SubmodelElement) => boolean,
    action?: (element: SubmodelElement) => void,
    maxDepth?: number,
  ): SubmodelElementNode[] {
    const result: SubmodelElementNode[] = [];
    this._elementMap.forEach((node) => {
      node.deepSearch(predicate, action, maxDepth).forEach((foundNode) => {
        result.push(foundNode);
      });
    });
    return result;
  }

  breadthFirstSearch(
    predicate?: (element: SubmodelElement) => boolean,
    action?: (element: SubmodelElement) => void,
    maxDepth?: number,
  ): SubmodelElementNode[] {
    const result: SubmodelElementNode[] = [];
    this._elementMap.forEach((node) => {
      node.breadthFirstSearch(predicate, action, maxDepth).forEach((foundNode) => {
        result.push(foundNode);
      });
    });
    return result;
  }

  toSubmodel(): Submodel {
    const submodel = this.submodel;
    submodel.submodelElements = Array.from(this._elementMap.values()).map(node => node.toSubmodelElement());
    return submodel;
  }
}
